import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 * Captures and reports errors, performance metrics, and user sessions
 */
export const initSentry = () => {
  // Only initialize in production or if explicitly enabled in development
  const isDevelopment = process.env.NODE_ENV === 'development';
  const sentryDSN = process.env.REACT_APP_SENTRY_DSN;

  // Skip if no DSN provided or in development without explicit enable
  if (!sentryDSN || (isDevelopment && !process.env.REACT_APP_SENTRY_ENABLED)) {
    console.log('[Sentry] Skipping initialization (no DSN or development mode)');
    return;
  }

  try {
    Sentry.init({
      dsn: sentryDSN,

      // Environment tracking
      environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development',

      // Release tracking (use git commit SHA if available)
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,

      // Performance monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Capture session replays for error debugging
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Sample rates
      tracesSampleRate: isDevelopment ? 1.0 : 0.1, // 100% in dev, 10% in prod
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Configure which errors to capture
      beforeSend(event, hint) {
        // Filter out non-critical errors in development
        if (isDevelopment) {
          // Log to console in development
          console.log('[Sentry] Captured error:', hint.originalException || hint.syntheticException);
        }

        // Don't send errors from browser extensions
        if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
          frame => frame.filename?.includes('extension://')
        )) {
          return null;
        }

        // Don't send errors from ad blockers
        if (event.message?.includes('blocked by client')) {
          return null;
        }

        return event;
      },

      // Ignore specific errors
      ignoreErrors: [
        // Browser extension errors
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        // Network errors (handled by application)
        'NetworkError',
        'Network request failed',
        // User cancellations
        'AbortError',
        'The user aborted a request',
        // ResizeObserver loop limit exceeded (benign)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],

      // Denylist for URLs to ignore
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
        // Browser internal
        /^about:/i,
      ],
    });

    console.log(`[Sentry] Initialized in ${process.env.NODE_ENV} mode`);
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
};

/**
 * Set user context for error tracking
 */
export const setSentryUser = (user) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.user_metadata?.full_name || user.email?.split('@')[0],
  });
};

/**
 * Set custom context tags
 */
export const setSentryContext = (key, data) => {
  Sentry.setContext(key, data);
};

/**
 * Add breadcrumb for debugging
 */
export const addSentryBreadcrumb = (message, category = 'user-action', level = 'info') => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Manually capture an exception
 */
export const captureSentryException = (error, context = {}) => {
  Sentry.captureException(error, {
    contexts: context,
  });
};

/**
 * Manually capture a message
 */
export const captureSentryMessage = (message, level = 'info') => {
  Sentry.captureMessage(message, level);
};

// Export Sentry instance for advanced usage
export { Sentry };
